import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';
import {
  SEGMENTED_CONTROL_PRESSED_SELECTOR,
  SEGMENTED_CONTROL_READY_CLASS,
  SEGMENTED_CONTROL_THUMB_CLASS,
  SEGMENTED_CONTROL_THUMB_WIDTH_PROPERTY,
  SEGMENTED_CONTROL_THUMB_X_PROPERTY,
} from 'src/app/core/helpers/ui/segmented-control.constant';

// Attaches to every `.segmented-control` and slides a single thumb under the pressed segment.
// Segments have content-driven widths, so the thumb's offset and width are measured at runtime
// and handed to CSS as custom properties. Selection state itself stays in `aria-pressed`.
@Directive({
  selector: '.segmented-control',
})
export class SegmentedControlDirective implements AfterViewInit, OnDestroy {
  private readonly host: HTMLElement;

  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private frame: number = 0;

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {
    this.host = elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    // Measuring and observing never changes Angular state, so keep it out of change detection.
    this.zone.runOutsideAngular(() => {
      this.mutationObserver = new MutationObserver(this.schedule);
      this.mutationObserver.observe(this.host, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-pressed'],
      });

      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.host);

      this.update();
      requestAnimationFrame(() => this.host.classList.add(SEGMENTED_CONTROL_READY_CLASS));
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  // Batches bursts of mutations (several aria-pressed flips in one change detection) into one read.
  private readonly schedule = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  };

  private update(): void {
    const pressed = this.host.querySelector<HTMLElement>(SEGMENTED_CONTROL_PRESSED_SELECTOR);
    const hasThumb = !!pressed && pressed.offsetWidth > 0;

    this.host.classList.toggle(SEGMENTED_CONTROL_THUMB_CLASS, hasThumb);
    if (!hasThumb) return;

    this.host.style.setProperty(SEGMENTED_CONTROL_THUMB_X_PROPERTY, `${pressed.offsetLeft}px`);
    this.host.style.setProperty(SEGMENTED_CONTROL_THUMB_WIDTH_PROPERTY, `${pressed.offsetWidth}px`);
  }
}
